/**
 * RouteDuur — de routeduur als eerste signaal op de kaart.
 *
 * Eén stuurgetal: de geplande dagduur (rijtijd + stoptijden uit de laatste
 * doorrekening) tegen het dagbudget. Daar stuurt kantoor de capaciteit op.
 * Geen uitloopfactor (besluit Willem 07-09): wat de planning zegt is wat de
 * kaart toont. De balk is de enige kleurdrager: groen < 85%, oranje 85-100%,
 * rood erboven; tekst wordt alleen rood boven budget. Eén statusregel: wat er
 * nog past of hoeveel erover heen, plus alleen als het speelt "wordt
 * doorgerekend" of "handmatige volgorde". Het moment van doorrekenen staat in
 * de tooltip.
 *
 * Bron van de velden: api/_lib/routes/route-reads.ts (laadLevendVelden).
 */
import clsx from 'clsx';
import type { RouteLevendVelden } from '../../lib/salesforce/types';
import { MetriekRij, metriekKleur } from './RouteMetriek';

export type DuurKleur = 'ok' | 'krap' | 'over' | null;

/** "2u48" boven het uur, "14 min" eronder, "10u" op het hele uur. */
export function minutenLabel(min: number | null | undefined): string | null {
  if (min == null || !Number.isFinite(min)) return null;
  const abs = Math.abs(Math.round(min));
  const u = Math.floor(abs / 60);
  const m = abs % 60;
  if (u === 0) return `${m} min`;
  if (m === 0) return `${u}u`;
  return `${u}u${String(m).padStart(2, '0')}`;
}

/** Kleur op de verwachte duur tegen het budget; null als er geen tijd is. */
export function duurKleur(r: RouteLevendVelden): DuurKleur {
  const verwacht = r.duurMinuten ?? r.verwachteDuurMinuten;
  const budget = r.dagBudgetMinuten ?? 600;
  if (verwacht == null) return null;
  const pct = verwacht / budget;
  if (pct > 1) return 'over';
  if (pct >= 0.85) return 'krap';
  return 'ok';
}

const balkKleur: Record<Exclude<DuurKleur, null>, string> = {
  ok: 'bg-emerald-500',
  krap: 'bg-amber-500',
  over: 'bg-red-500',
};

function geledenLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min} min geleden`;
  const uur = Math.round(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  return `${Math.round(uur / 24)} d geleden`;
}

/** "nog 1u20 vrij" of "1u15 boven budget". */
function ruimteLabel(verwacht: number | null | undefined, budget: number): { tekst: string; over: boolean } | null {
  if (verwacht == null) return null;
  const rest = budget - verwacht;
  if (rest >= 0) return { tekst: `nog ${minutenLabel(rest)} vrij`, over: false };
  return { tekst: `${minutenLabel(rest)} boven budget`, over: true };
}

/**
 * "Loopt 18 min voor" / "loopt 25 min achter" (plan 2026-10-12): de ETA van
 * de eerstvolgende stop tegen de planstand bij vertrek. Grijs onder 15 min,
 * oranje 15-30, rood erboven. Null zolang er geen herijk is geweest.
 */
export function voorAchterLabel(r: RouteLevendVelden): { tekst: string; kleur: 'grijs' | 'oranje' | 'rood' } | null {
  const m = r.voorAchterMinuten;
  if (m == null || !Number.isFinite(m)) return null;
  const abs = Math.abs(Math.round(m));
  const kleur = abs > 30 ? 'rood' : abs >= 15 ? 'oranje' : 'grijs';
  if (abs < 3) return { tekst: 'loopt op schema', kleur };
  return { tekst: `loopt ${abs} min ${m < 0 ? 'voor' : 'achter'}`, kleur };
}

const voorAchterKleur = { grijs: 'text-gray-500', oranje: 'text-amber-700', rood: 'text-red-700' } as const;

/** Alles wat níét op de kaart hoeft maar wel te vinden moet zijn. */
function tooltip(route: RouteLevendVelden, budget: number): string {
  const regels: string[] = [];
  const duur = minutenLabel(route.duurMinuten ?? route.verwachteDuurMinuten);
  if (duur) regels.push(`Gepland ${duur} van ${minutenLabel(budget)} dagbudget (rijtijd + stoptijden)`);
  const geleden = geledenLabel(route.berekendOp);
  if (geleden) regels.push(`Doorgerekend ${geleden}`);
  const va = voorAchterLabel(route);
  const herijkt = geledenLabel(route.herijktOp);
  if (va) regels.push(`Onderweg: ${va.tekst}${herijkt ? ` (herijkt ${herijkt})` : ''}`);
  if (route.autoUpdatesPauze) regels.push(`Klantupdates gepauzeerd${route.autoUpdatesPauzeReden ? `: ${route.autoUpdatesPauzeReden}` : ''}`);
  return regels.join('\n');
}

interface RouteDuurProps {
  route: RouteLevendVelden;
  variant?: 'card' | 'compact' | 'header';
  /** Route is afgerond of onderweg: geen "wacht"-stand tonen. */
  stil?: boolean;
}

export function RouteDuur({ route, variant = 'card', stil = false }: RouteDuurProps) {
  const kleur = duurKleur(route);
  const verwachtMin = route.duurMinuten ?? route.verwachteDuurMinuten ?? null;
  const verwacht = minutenLabel(verwachtMin);
  const budget = route.dagBudgetMinuten ?? 600;
  const pct = verwachtMin != null ? Math.min(100, Math.round((verwachtMin / budget) * 100)) : 0;
  const wacht = !stil && route.levendStand === 'wacht';
  const nieuw = route.stopsNaBerekening ?? 0;
  const ruimte = ruimteLabel(verwachtMin, budget);
  const title = tooltip(route, budget);
  const voorAchter = voorAchterLabel(route);
  const herijktGeleden = geledenLabel(route.herijktOp);

  if (variant === 'compact') {
    if (!verwacht) return <span className="text-[9px] text-gray-400 italic">geen tijd</span>;
    return (
      <span
        className={clsx('inline-flex items-center gap-1 text-[10px] font-medium', kleur === 'over' ? 'text-red-700' : 'text-gray-600')}
        title={title}
      >
        <span className={clsx('w-1.5 h-1.5 rounded-full', kleur ? balkKleur[kleur] : 'bg-gray-300')} />
        {verwacht}
        {wacht && <span className="text-amber-600" title="wordt doorgerekend">·</span>}
      </span>
    );
  }

  const isHeader = variant === 'header';

  if (!verwacht) {
    return (
      <div className={clsx(isHeader ? 'min-w-[260px] text-sm' : 'px-3 py-2 border-t border-gray-100 text-xs', 'text-gray-400 italic')}>
        {wacht ? 'wordt doorgerekend…' : 'nog geen tijd'}
      </div>
    );
  }

  // Statusregel rechts: alleen als er iets aan de hand is.
  const stand = wacht ? (
    <span
      className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded font-medium whitespace-nowrap min-w-0 truncate"
      title={nieuw > 0 ? `${nieuw} stop${nieuw === 1 ? '' : 's'} nog niet doorgerekend` : 'wordt doorgerekend'}
    >
      {nieuw > 0 ? `+${nieuw} · ` : ''}doorrekenen
    </span>
  ) : route.handmatigeVolgorde ? (
    <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded whitespace-nowrap min-w-0 truncate" title="Volgorde handmatig gezet in de planner; automatisch worden alleen de tijden bijgewerkt">
      handmatige volgorde
    </span>
  ) : null;

  // Kaart: dezelfde rij als Ophalen/Afleveren (RouteMetriek). Header (planner)
  // houdt de bredere weergave met de ruimte-regel eronder.
  if (!isHeader) {
    const merk = voorAchter && voorAchter.kleur !== 'grijs' ? (
      <span
        className={clsx('text-[9px] px-1 py-0.5 rounded whitespace-nowrap', voorAchter.kleur === 'rood' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700')}
        title={`Onderweg: ${voorAchter.tekst}`}
      >
        {voorAchter.tekst.replace('loopt ', '')}
      </span>
    ) : wacht ? (
      <span
        className="text-[9px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded whitespace-nowrap"
        title={nieuw > 0 ? `${nieuw} stop${nieuw === 1 ? '' : 's'} nog niet doorgerekend` : 'wordt doorgerekend'}
      >
        {nieuw > 0 ? `+${nieuw}` : '~'}
      </span>
    ) : route.handmatigeVolgorde ? (
      <span
        className="text-[9px] bg-gray-100 text-gray-600 px-1 py-0.5 rounded whitespace-nowrap"
        title="Volgorde handmatig gezet in de planner; automatisch worden alleen de tijden bijgewerkt"
      >
        vast
      </span>
    ) : null;
    return (
      <MetriekRij
        label="Dagduur"
        waarde={verwacht}
        totaal={minutenLabel(budget)}
        pct={pct}
        kleur={metriekKleur(verwachtMin, budget)}
        overschreden={verwachtMin != null && verwachtMin > budget}
        title={title}
        merk={merk}
      />
    );
  }

  return (
    <div className="min-w-[280px]" title={title}>
      <div className="flex items-baseline justify-between gap-2 text-base">
        <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Dagduur</span>
        <span className="tabular-nums whitespace-nowrap">
          <span className={clsx('font-semibold', kleur === 'over' ? 'text-red-700' : 'text-gray-900')}>{verwacht}</span>
          <span className="text-gray-400 text-xs"> / {minutenLabel(budget)}</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
        <div className={clsx('h-full rounded-full', kleur ? balkKleur[kleur] : 'bg-gray-400')} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] tabular-nums">
        {ruimte && (
          <span className={clsx('font-medium whitespace-nowrap', ruimte.over ? 'text-red-700' : 'text-gray-500')}>
            {ruimte.tekst}
          </span>
        )}
        {stand}
      </div>
      {voorAchter && (
        <div className={clsx('mt-0.5 text-[10px] tabular-nums', voorAchterKleur[voorAchter.kleur])}>
          {voorAchter.tekst}
          {herijktGeleden ? <span className="text-gray-400"> · herijkt {herijktGeleden}</span> : null}
          {route.autoUpdatesPauze ? <span className="ml-1 bg-gray-100 text-gray-600 px-1 py-0.5 rounded">updates gepauzeerd</span> : null}
        </div>
      )}
    </div>
  );
}
